import _slicedToArray from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useId } from '@reach/auto-id';
import { useEffect, useRef, useState } from 'react';
import { mergeRefs } from '../utils';
import { usePopper } from './usePopper';
export var useControls = function useControls(props) {
  var context = props.context,
      _props$placement = props.placement,
      placementProp = _props$placement === void 0 ? 'bottom-start' : _props$placement,
      onChange = props.onChange,
      selectedValue = props.selectedValue; // If this menu is a nested menu, that means there's a parent menu context

  var parentMenu = context; // Check if this menu is a nested menu or top level menu

  var hasParentMenu = Boolean(parentMenu);

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      focusedIndex = _useState2[0],
      setFocusedIndex = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isOpen = _useState4[0],
      setIsOpen = _useState4[1];

  var menuId = "select-".concat(useId());
  var buttonId = "selectbutton-".concat(useId());
  var focusableItems = useRef(null);
  var menuRef = useRef(null);
  var buttonRef = useRef(null);

  var _usePopper = usePopper({
    placement: !hasParentMenu ? placementProp : 'right-start',
    forceUpdate: isOpen,
    preventOverflow: true,
    flip: false,
    offset: !hasParentMenu ? [0, 4] : [0, 0],
    fixed: true
  }),
      placement = _usePopper.placement,
      popper = _usePopper.popper,
      reference = _usePopper.reference;

  useEffect(function () {
    if (isOpen && menuRef && menuRef.current) {
      var focusables = Array.from(menuRef.current.querySelectorAll('[role="option"]'));
      focusableItems.current = menuRef.current ? focusables : [];
      initTabIndex();
    }
  }, [isOpen, menuRef]);

  var onOpen = function onOpen() {
    return setIsOpen(true);
  };

  var onClose = function onClose() {
    setFocusedIndex(-1);
    setIsOpen(false);
    buttonRef.current.focus();
  };

  var onCloseAll = function onCloseAll() {
    onClose();
    var next = parentMenu;

    while (next != null) {
      next.onClose();
      next = next.parentMenu;
    }
  };

  var openAndFocusMenu = function openAndFocusMenu() {
    onOpen();

    if (menuRef.current) {
      menuRef.current.focus();
    }
  };

  var updateTabIndex = function updateTabIndex(index) {
    if (focusableItems.current.length > 0) {
      var nodeAtIndex = focusableItems.current[index];
      focusableItems.current.forEach(function (node) {
        if (node !== nodeAtIndex) {
          node.setAttribute('tabindex', -1);
        }
      });
      nodeAtIndex.setAttribute('tabindex', 0);
    }
  };

  var initTabIndex = function initTabIndex() {
    focusableItems.current.forEach(function (node, index) {
      return index === 0 && node.setAttribute('tabindex', 0);
    });
  };

  var findSelectedOptionIndex = function findSelectedOptionIndex() {
    return focusableItems.current.findIndex(function (node) {
      return node.getAttribute('aria-selected') === 'true';
    });
  };

  useEffect(function () {
    if (focusedIndex !== -1) {
      if (focusableItems.current[focusedIndex]) {
        focusableItems.current[focusedIndex].focus();
      }

      updateTabIndex(focusedIndex);
    }

    if (focusedIndex === -1 && isOpen) {
      var selectedIndex = findSelectedOptionIndex();

      if (selectedIndex === -1) {
        setFocusedIndex(0);
      } else {
        setFocusedIndex(selectedIndex);
      }
    }
  }, [focusedIndex, isOpen, buttonRef, menuRef]);

  var handleClick = function handleClick(e) {
    if (menuRef.current.contains(e.target)) {
      return;
    }

    onClose();
  };

  useEffect(function () {
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
    }

    return function () {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen]);
  return {
    popper: popper,
    reference: reference,
    focusedIndex: focusedIndex,
    setFocusedIndex: setFocusedIndex,
    openAndFocusMenu: openAndFocusMenu,
    isOpen: isOpen,
    onOpen: onOpen,
    onClose: onClose,
    onCloseAll: onCloseAll,
    buttonRef: mergeRefs(buttonRef, reference.ref),
    menuRef: mergeRefs(menuRef, popper.ref),
    focusableItems: focusableItems,
    placement: placement,
    menuId: menuId,
    buttonId: buttonId,
    closeOnBlur: true,
    parentMenu: parentMenu,
    hasParentMenu: hasParentMenu,
    onChange: onChange,
    selectedValue: selectedValue
  };
};